// 圖示庫入口
// 1) 內建圖示：library.js（分組維護）
// 2) 自訂圖示：把 .svg 檔丟進 src/icons/svg/，檔名（kebab-case）即圖示名，自動註冊
// 3) 舊資料相容：Element Plus 圖示名（如 Setting、HomeFilled）會被對應到本庫圖示

import { ICON_GROUPS } from './library'

const DEFAULT_VIEW_BOX = '0 0 24 24'

// ---- 內建圖示 ----
const icons = {}
const groups = ICON_GROUPS.map((group) => {
  const names = []
  for (const [name, content] of Object.entries(group.icons)) {
    icons[name] = { name, viewBox: DEFAULT_VIEW_BOX, content, custom: false }
    names.push(name)
  }
  return { key: group.key, title: group.title, names }
})

// ---- 自訂圖示（src/icons/svg/*.svg）----
const svgFiles = import.meta.glob('./svg/*.svg', { query: '?raw', import: 'default', eager: true })
const customNames = []
for (const [file, raw] of Object.entries(svgFiles)) {
  const name = file.replace(/^.*\/(.+)\.svg$/, '$1')
  const viewBox = raw.match(/viewBox="([^"]+)"/)?.[1] || DEFAULT_VIEW_BOX
  const content = raw.match(/<svg[^>]*>([\s\S]*)<\/svg>/)?.[1]?.trim() || ''
  if (!content) continue
  icons[name] = { name, viewBox, content, custom: true }
  customNames.push(name)
}
if (customNames.length) {
  groups.push({ key: 'custom', title: '自訂圖示', names: customNames.sort() })
}

// ---- 舊圖示名對應（Element Plus / 早期資料）----
const ALIASES = {
  setting: 'settings',
  tools: 'tool',
  homefilled: 'home',
  house: 'home',
  odometer: 'dashboard',
  menu: 'menu',
  operation: 'sliders',
  userfilled: 'users',
  avatar: 'user',
  usergroup: 'users',
  document: 'file-text',
  documentcopy: 'files',
  files: 'files',
  folderopened: 'folder-open',
  picture: 'image',
  picturefilled: 'image',
  videocamera: 'video',
  uploadfilled: 'upload',
  download: 'download',
  dataline: 'chart-bar',
  dataanalysis: 'chart-line',
  piechart: 'chart-pie',
  histogram: 'chart-bar',
  coin: 'database',
  monitor: 'monitor',
  cloudy: 'cloud',
  bell: 'bell',
  message: 'mail',
  chatdotround: 'message',
  promotion: 'send',
  sharing: 'share',
  connection: 'link',
  search: 'search',
  plus: 'plus',
  edit: 'edit',
  editpen: 'edit',
  delete: 'trash',
  refresh: 'refresh',
  filter: 'filter',
  view: 'eye',
  close: 'close',
  printer: 'printer',
  ticket: 'clipboard',
  calendar: 'calendar',
  timer: 'clock',
  star: 'star',
  collectiontag: 'tag',
  location: 'map-pin',
  reading: 'book',
  goods: 'package',
  shoppingcart: 'cart',
  creditcard: 'credit-card',
  present: 'gift',
  officebuilding: 'building',
  suitcase: 'briefcase',
  van: 'truck',
  infofilled: 'info',
  warning: 'alert',
  warningfilled: 'alert',
  question: 'help',
  questionfilled: 'help',
  sunny: 'sun',
  moon: 'moon',
  brush: 'palette',
  lock: 'lock',
  unlock: 'unlock',
  key: 'key',
  switchbutton: 'power',
  list: 'list'
}

// 未知圖示的預設值（避免選單出現空白）
export const FALLBACK_ICON = 'menu'

/** 將任意寫法（PascalCase、底線、Element Plus 名）正規化為本庫圖示名 */
export function normalizeIconName(raw) {
  if (!raw) return ''
  const value = String(raw).trim()
  if (icons[value]) return value

  const kebab = value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
  if (icons[kebab]) return kebab

  const flat = value.toLowerCase().replace(/[-_\s]/g, '')
  return ALIASES[flat] || ALIASES[kebab] || ''
}

/** 取得圖示定義；找不到時回傳預設圖示 */
export function getIcon(name) {
  const key = normalizeIconName(name)
  return icons[key] || icons[FALLBACK_ICON]
}

/** 圖示是否存在於本庫 */
export function hasIcon(name) {
  return Boolean(normalizeIconName(name))
}

export const ICONS = icons
export const ICON_NAMES = Object.keys(icons)
export const ICON_GROUP_LIST = groups
